// WorkshopNavigation.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Home, Settings, ChevronDown, ChevronRight, LayoutList, Briefcase, AlertTriangle,
  FileText, Target, Search, Eye
} from 'lucide-react';
import Atelier1Navigation from './navigation/Atelier1Navigation';
import Atelier2Navigation from './navigation/Atelier2Navigation';
import Atelier3Navigation from './navigation/Atelier3Navigation';
import Atelier4Navigation from './navigation/Atelier4Navigation';
import Atelier5Navigation from './navigation/Atelier5Navigation';

import { getUnsavedChanges, setNavigationWarningDialogOpen, TAB_ORDER, isTabCompleted } from '../utils/navigationUtils';

const WorkshopNavigation = ({
  activeTab,
  setActiveTab,
  expandedWorkshops,
  toggleWorkshop,
  isSidebarOpen,
  completedTabs
}) => {
  const { t } = useTranslation();
  // Professional navigation styles with enhanced readability and smooth animations
  const navItemClass = "w-full flex items-center px-4 py-3 rounded-xl transition-all duration-300 text-sm group relative overflow-hidden";
  const activeStyle = "bg-gradient-to-r from-blue-600/90 to-indigo-600/90 text-white font-semibold shadow-lg transform scale-[1.01] border border-blue-400/30 ring-1 ring-blue-300/20";
  const inactiveStyle = "text-gray-200 hover:bg-gradient-to-r hover:from-slate-700/60 hover:to-slate-600/60 hover:text-white hover:transform hover:scale-[1.005] hover:shadow-md hover:border hover:border-slate-500/30";
  const workshopTitleClass = "w-full flex items-center px-4 py-3 rounded-xl transition-all duration-300 text-sm font-medium group relative overflow-hidden border border-transparent hover:border-slate-500/30 hover:shadow-md hover:bg-slate-700/40";
  const subItemClass = `${navItemClass} pl-8 py-2.5 text-xs ml-0`;

  // Helper to calculate the percentage completed for a given workshop
  const calculateProgress = (workshopKey) => {
    if (!completedTabs) return 0;
    const tabs = TAB_ORDER[workshopKey] || [];
    const validTabs = tabs.filter(t => t !== 'dashboard');
    if (validTabs.length === 0) return 0;

    const completedCount = validTabs.filter(t => isTabCompleted(t, completedTabs)).length;
    return Math.round((completedCount / validTabs.length) * 100);
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-20 w-72 backdrop-blur-xl border-r border-white/10 p-5 overflow-y-auto transition-all duration-500 ease-out ${isSidebarOpen ? 'translate-x-0 sidebar-slide-in' : '-translate-x-full sidebar-slide-out'} shadow-xl custom-scrollbar`}
      style={{
        minWidth: '18rem',
        maxWidth: '18rem',
        flexShrink: 0,
        background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.94) 50%, rgba(51, 65, 85, 0.92) 100%)',
        backdropFilter: 'blur(16px)',
        borderImage: 'linear-gradient(145deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06)) 1',
        scrollBehavior: 'smooth'
      }}
    >
      {/* Professional Sidebar Header */}
      <div className="mb-6 animate-fade-in">
        {/* Enhanced Logo Section */}
        <div className="flex items-center p-3 rounded-xl mb-5 shadow-md border border-slate-600/30 bg-slate-800/40 hover:bg-slate-700/50 hover:border-slate-500/40 transition-all duration-300 group">
          {/* ACG Logo */}
          <div className="overflow-hidden rounded-lg shadow-md bg-white p-2 transition-all duration-300 hover:scale-105 mr-3">
            <img
              src="/acg-logo.png"
              alt="ACG Logo"
              className="h-7 w-auto object-contain"
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center">
              <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-2 shadow-sm transition-all duration-300 group-hover:shadow-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white group-hover:text-blue-100 transition-colors duration-300">Navigation</h3>
              </div>
            </div>
          </div>
        </div>
        {/* Professional Separator */}
        <div className="relative h-px bg-gradient-to-r from-transparent via-slate-500/40 to-transparent mb-5">
          <div className="absolute left-1/2 top-1/2 w-1.5 h-1.5 bg-blue-400/80 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>
      </div>
      <nav className="w-full">
        <ul className="space-y-1 w-full">
          {/* Professional Dashboard Button */}
          <li className="w-full mb-4">
            <button
              className={`${navItemClass} ${activeTab === 'dashboard' ? activeStyle : inactiveStyle} group`}
              onClick={() => {
                // Check for unsaved changes before navigation
                if (getUnsavedChanges()) {
                  // Store the target tab in a data attribute
                  setNavigationWarningDialogOpen(true);
                  // The actual navigation will be handled by the dialog
                  return;
                }
                // If no unsaved changes, navigate directly
                setActiveTab('dashboard');
              }}
            >
              <div className="flex items-center w-full">
                <div className={`p-2 rounded-lg transition-all duration-300 mr-3 ${activeTab === 'dashboard'
                  ? 'bg-white/20 shadow-md'
                  : 'bg-slate-700/50 group-hover:bg-slate-600/60'
                  }`}>
                  <Home size={16} className={`flex-shrink-0 transition-all duration-300 ${activeTab === 'dashboard'
                    ? 'text-white'
                    : 'text-gray-200 group-hover:text-white'
                    }`} />
                </div>
                <div className="flex-1">
                  <span className={`font-medium transition-colors duration-300 ${activeTab === 'dashboard' ? 'text-white' : 'text-gray-200 group-hover:text-white'
                    }`}>{t('navigation.dashboard', 'Tableau de bord')}</span>
                  <div className={`text-xs transition-colors duration-300 ${activeTab === 'dashboard' ? 'text-blue-100' : 'text-gray-400 group-hover:text-gray-300'
                    }`}>{t('navigation.overview', 'Vue d\'ensemble')}</div>
                </div>
                {activeTab === 'dashboard' && (
                  <div className="w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                )}
              </div>
            </button>
          </li>

          {/* Professional Separator */}
          <li className="my-5 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-600/30"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 py-1 text-xs text-white uppercase font-medium rounded-md border border-slate-600/40 bg-slate-800/60 hover:border-slate-500/50 hover:bg-slate-700/70 transition-all duration-300">
                {t('navigation.workshops', 'Ateliers')}
              </span>
            </div>
          </li>

          {/* Atelier 1 */}
          <Atelier1Navigation
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            expanded={expandedWorkshops['workshop1']}
            toggleExpand={() => toggleWorkshop('workshop1')}
            baseNavItemClass={navItemClass}
            workshopTitleClass={workshopTitleClass}
            activeStyle={activeStyle}
            inactiveStyle={inactiveStyle}
            completedTabs={completedTabs}
            progress={calculateProgress('workshop1')}
          />

          {/* Atelier 2 */}
          <Atelier2Navigation
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            expanded={expandedWorkshops['workshop2']}
            toggleExpand={() => toggleWorkshop('workshop2')}
            baseNavItemClass={navItemClass}
            workshopTitleClass={workshopTitleClass}
            activeStyle={activeStyle}
            inactiveStyle={inactiveStyle}
            completedTabs={completedTabs}
            progress={calculateProgress('workshop2')}
          />

          {/* Atelier 3 */}
          <Atelier3Navigation
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            expanded={expandedWorkshops['workshop3']}
            toggleExpand={() => toggleWorkshop('workshop3')}
            baseNavItemClass={navItemClass}
            workshopTitleClass={workshopTitleClass}
            activeStyle={activeStyle}
            inactiveStyle={inactiveStyle}
            completedTabs={completedTabs}
            progress={calculateProgress('workshop3')}
          />

          {/* Atelier 4 */}
          <Atelier4Navigation
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            expanded={expandedWorkshops['workshop4']}
            toggleExpand={() => toggleWorkshop('workshop4')}
            baseNavItemClass={navItemClass}
            workshopTitleClass={workshopTitleClass}
            activeStyle={activeStyle}
            inactiveStyle={inactiveStyle}
            completedTabs={completedTabs}
            progress={calculateProgress('workshop4')}
          />

          {/* Atelier 5 */}
          <Atelier5Navigation
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            expanded={expandedWorkshops['workshop5']}
            toggleExpand={() => toggleWorkshop('workshop5')}
            baseNavItemClass={navItemClass}
            workshopTitleClass={workshopTitleClass}
            activeStyle={activeStyle}
            inactiveStyle={inactiveStyle}
            completedTabs={completedTabs}
            progress={calculateProgress('workshop5')}
          />
        </ul>
      </nav>
    </aside>
  );
};

export default WorkshopNavigation;